import { useState, useEffect } from 'react'
import { useConfirmDelete } from '../hooks/useConfirmDelete'
import { useSearchParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { rapportiniApi, workOrdersApi, committentiApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { FileText, CheckCircle, XCircle, Eye, Plus, Pencil, Trash2, Send, Download, ExternalLink, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'

/* ── Badge styles — light theme ───────────────────────────── */
type BS = { bg: string; color: string; border: string }

const STATO_STYLE: Record<string, BS> = {
  bozza:          { bg: '#f1f5f9', color: '#64748b', border: '#e2e8f0' },
  inviato:        { bg: '#dbeafe', color: '#1d4ed8', border: '#bfdbfe' },
  in_approvazione:{ bg: '#fef3c7', color: '#b45309', border: '#fde68a' },
  approvato:      { bg: '#dcfce7', color: '#15803d', border: '#bbf7d0' },
  rifiutato:      { bg: '#fee2e2', color: '#b91c1c', border: '#fecaca' },
}
const ESITO_STYLE: Record<string, BS> = {
  completato:              { bg: '#dcfce7', color: '#15803d', border: '#bbf7d0' },
  parzialmente_completato: { bg: '#fef3c7', color: '#b45309', border: '#fde68a' },
  non_completato:          { bg: '#fee2e2', color: '#b91c1c', border: '#fecaca' },
}
const fallback: BS = { bg: '#f1f5f9', color: '#64748b', border: '#e2e8f0' }

function Pill({ label, s }: { label: string; s: BS }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      whiteSpace: 'nowrap' as const,
    }}>
      {label}
    </span>
  )
}

interface RapportinoItem {
  id: number; work_order_id: number; data_intervento: string
  esito?: string; descrizione_lavori?: string
  parametri_tecnici?: Record<string, unknown>
  apparati_installati?: { seriale: string; modello: string }[]
  note_aggiuntive?: string; durata_minuti?: number; km_percorsi?: number; stato: string
  wo_numero?: string; pdf_url?: string; firma_cliente_nome?: string
  committente_email?: string | null; tecnico_nome?: string | null
}

/* ── Table header helper ──────────────────────────────────── */
function TH({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' | 'center' }) {
  return (
    <th style={{
      padding: '10px 16px', textAlign: align,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
      color: 'var(--text-500)', fontFamily: "'Barlow Semi Condensed', sans-serif",
    }}>
      {children}
    </th>
  )
}

/* ── Main page ──────────────────────────────────────────── */
export default function Rapportini() {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const [searchParams] = useSearchParams()
  const [statoFilter, setStatoFilter] = useState(searchParams.get('stato') ?? '')
  const [committenteFilter, setCommittenteFilter] = useState('')
  const [search, setSearch] = useState('')
  const [dataDa, setDataDa] = useState(format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd'))
  const [dataA, setDataA] = useState(format(new Date(), 'yyyy-MM-dd'))

  useEffect(() => {
    const s = searchParams.get('stato') ?? ''
    setStatoFilter(s)
  }, [searchParams])
  const [showCreate, setShowCreate]   = useState(false)
  const [editing, setEditing]         = useState<RapportinoItem | null>(null)
  const [dettaglio, setDettaglio]     = useState<RapportinoItem | null>(null)
  const qc = useQueryClient()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'
  const isTecnico = user?.role === 'tecnico' || user?.role === 'tecnico_senior'
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const toggleSelect = (id: number) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  const toggleAll = () => setSelected(s => s.size === items.length ? new Set() : new Set(items.map(r => r.id)))
  const [approvaModal, setApprovaModal] = useState<{ id: number; work_order_id: number; committente_email: string | null } | null>(null)
  const [inviaEmailCheck, setInviaEmailCheck] = useState(true)

  const { data: committentiData } = useQuery({
    queryKey: ['committenti-select'],
    queryFn: () => committentiApi.list({ per_page: 100 }).then(r => r.data.items),
  })
  const committenti: any[] = committentiData ?? []

  const { data, isLoading } = useQuery({
    queryKey: ['rapportini', statoFilter, committenteFilter, search, dataDa, dataA],
    queryFn: () => rapportiniApi.list({
      stato: statoFilter || undefined,
      committente_id: committenteFilter ? Number(committenteFilter) : undefined,
      search: search || undefined,
      data_da: dataDa || undefined,
      data_a: dataA || undefined,
      per_page: 500,
    }).then(r => r.data),
  })

  const invia  = useMutation({ mutationFn: (id: number) => rapportiniApi.invia(id),  onSuccess: () => { qc.invalidateQueries({ queryKey: ['rapportini'] }); toast.success('Inviato per approvazione') } })
  const approva = useMutation({
    mutationFn: ({ id, inviaEmail }: { id: number; work_order_id: number; inviaEmail: boolean }) =>
      rapportiniApi.approva(id, inviaEmail),
    onSuccess: (res, { work_order_id }) => {
      qc.invalidateQueries({ queryKey: ['rapportini'] })
      qc.invalidateQueries({ queryKey: ['work-order', String(work_order_id)] })
      qc.invalidateQueries({ queryKey: ['work-orders'] })
      const msg = res.data?.email_inviata ? '✓ Approvato — email inviata al committente' : '✓ Approvato'
      toast.success(msg)
      setApprovaModal(null)
    },
  })
  const rifiuta = useMutation({
    mutationFn: ({ id }: { id: number; work_order_id: number }) => rapportiniApi.rifiuta(id),
    onSuccess: (_, { work_order_id }) => {
      qc.invalidateQueries({ queryKey: ['rapportini'] })
      qc.invalidateQueries({ queryKey: ['work-order', String(work_order_id)] })
      qc.invalidateQueries({ queryKey: ['work-orders'] })
      toast.success('Rifiutato')
    },
  })
  const elimina = useMutation({ mutationFn: (id: number) => rapportiniApi.delete(id), onSuccess: () => { qc.invalidateQueries({ queryKey: ['rapportini'] }); toast.success('Eliminato') } })

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: number[]) => rapportiniApi.bulkDelete(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rapportini'] })
      setSelected(new Set())
      toast.success('Rapportini eliminati')
    },
    onError: () => toast.error('Errore eliminazione'),
  })

  const deleteAllMutation = useMutation({
    mutationFn: () => rapportiniApi.deleteAll(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rapportini'] })
      setSelected(new Set())
      toast.success('Tutti i rapportini eliminati')
    },
    onError: () => toast.error('Errore eliminazione'),
  })

  const items: RapportinoItem[] = data?.items ?? []

  const [zipModalOpen, setZipModalOpen] = useState(false)
  const [zipAnno, setZipAnno] = useState(new Date().getFullYear())
  const [zipMese, setZipMese] = useState(new Date().getMonth() + 1)
  const [zipLoading, setZipLoading] = useState(false)

  const MESI = ['','Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno',
                'Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre']

  const handleDownloadZip = async () => {
    setZipLoading(true)
    try {
      const res = await rapportiniApi.downloadZip(zipAnno, zipMese)
      const blob = new Blob([res.data], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `rapportini_${zipAnno}_${String(zipMese).padStart(2,'0')}.zip`
      a.click()
      window.URL.revokeObjectURL(url)
      setZipModalOpen(false)
      toast.success('Download ZIP completato')
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Errore download ZIP')
    } finally {
      setZipLoading(false)
    }
  }

  const handleDownloadPdf = async (id: number, numero: string) => {
    try {
      const res = await rapportiniApi.getPdf(id)
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `${numero}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err: any) {
      if (err.response?.status === 404) {
        toast.error('PDF non ancora generato per questo rapportino')
      } else {
        toast.error('Errore download PDF')
      }
    }
  }

  return (
    <div className="space-y-5">
      {ConfirmModal}
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-up">
        <div>
          <h1 className="page-title">Rapportini</h1>
          <p className="page-subtitle">{data?.total ?? 0} rapportini totali</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {isAdmin && selected.size > 0 && (
            <button
              onClick={() => confirmDelete(`Eliminare ${selected.size} rapportini selezionati?`, () => bulkDeleteMutation.mutate(Array.from(selected)))}
              className="btn-secondary" style={{ color: '#ef4444' }}
              disabled={bulkDeleteMutation.isPending}
            >
              <Trash2 size={15} /><span className="ml-1">Elimina {selected.size}</span>
            </button>
          )}
          {isAdmin && selected.size > 0 && (
            <button onClick={() => setSelected(new Set())} className="btn-ghost text-sm flex items-center gap-1">
              <X size={13} /> Deseleziona
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => confirmDelete('Eliminare TUTTI i rapportini? Operazione irreversibile.', () => deleteAllMutation.mutate())}
              className="btn-secondary" style={{ color: '#ef4444' }}
              disabled={deleteAllMutation.isPending}
            >
              <Trash2 size={15} /><span className="ml-1">Cancella tutto</span>
            </button>
          )}
          {!isTecnico && (
            <button onClick={() => setZipModalOpen(true)} className="btn-secondary flex items-center gap-1.5">
              <Download size={15} /> ZIP Mensile
            </button>
          )}
          {!isTecnico && (
            <button onClick={() => setShowCreate(true)} className="btn-primary">
              <Plus size={15} /> Nuovo Rapportino
            </button>
          )}
        </div>
      </div>

      {/* Filter */}
      <div className="card p-4 animate-fade-up" style={{ animationDelay: '0.05s' }}>
        <div className="flex flex-wrap gap-3">
          <input
            type="text"
            className="input w-64"
            placeholder="Cerca per pratica, descrizione, cliente…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select className="select w-48" value={statoFilter} onChange={(e) => setStatoFilter(e.target.value)}>
            <option value="">Tutti gli stati</option>
            {['bozza','inviato','in_approvazione','approvato','rifiutato'].map(s => (
              <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <select className="select w-52" value={committenteFilter} onChange={e => setCommittenteFilter(e.target.value)}>
            <option value="">Tutti i committenti</option>
            {committenti.map((c: any) => (
              <option key={c.id} value={c.id}>{c.ragione_sociale}</option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <label className="label" style={{ marginBottom: 0 }}>Dal</label>
            <input type="date" className="input" value={dataDa} onChange={e => setDataDa(e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <label className="label" style={{ marginBottom: 0 }}>Al</label>
            <input type="date" className="input" value={dataA} onChange={e => setDataA(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden animate-fade-up" style={{ animationDelay: '0.1s' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '10px 12px', width: 36 }}>
                  <input type="checkbox" checked={items.length > 0 && selected.size === items.length} onChange={toggleAll} className="cursor-pointer" />
                </th>
                <TH>Pratica</TH>
                <TH>Data</TH>
                <TH>Tecnico</TH>
                <TH>Cliente firmante</TH>
                <TH>Esito</TH>
                <TH>Stato</TH>
                <TH align="center">Azioni</TH>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={8} className="py-12 text-center">
                  <div className="flex items-center justify-center gap-2" style={{ color: '#9ca3af' }}>
                    <div className="w-4 h-4 rounded-full animate-spin" style={{ border: '2px solid #e5e7eb', borderTopColor: '#1E40AF' }} />
                    Caricamento…
                  </div>
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="py-12 text-center" style={{ color: '#9ca3af', fontSize: 13 }}>
                  Nessun rapportino trovato
                </td></tr>
              ) : items.map((r) => (
                <tr key={r.id} className="table-row-hover" style={{ borderBottom: '1px solid #f3f4f6', background: selected.has(r.id) ? 'rgba(26,115,232,0.05)' : undefined }}>
                  <td style={{ padding: '11px 12px', width: 36 }}><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} className="cursor-pointer" /></td>
                  <td style={{ padding: '11px 16px' }}>
                    <Link to={`/pratiche/${r.work_order_id}`} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: '#1E40AF' }}>
                        {r.wo_numero || `WO-${r.work_order_id}`}
                      </span>
                      <ExternalLink size={11} color="#9ca3af" />
                    </Link>
                  </td>
                  <td style={{ padding: '11px 16px', fontSize: 12, color: '#6b7280' }}>
                    {format(new Date(r.data_intervento), 'dd/MM/yyyy', { locale: it })}
                  </td>
                  <td style={{ padding: '11px 16px', fontSize: 12, color: '#374151' }}>
                    {r.tecnico_nome || '—'}
                  </td>
                  <td style={{ padding: '11px 16px', fontSize: 12, color: '#374151' }}>
                    {r.firma_cliente_nome || '—'}
                  </td>
                  <td style={{ padding: '11px 16px' }}>
                    {r.esito
                      ? <Pill label={r.esito.replace(/_/g, ' ')} s={ESITO_STYLE[r.esito] ?? fallback} />
                      : <span style={{ color: '#d1d5db' }}>—</span>}
                  </td>
                  <td style={{ padding: '11px 16px' }}>
                    <Pill label={r.stato.replace(/_/g, ' ')} s={STATO_STYLE[r.stato] ?? fallback} />
                  </td>
                  <td style={{ padding: '11px 16px' }}>
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => setDettaglio(r)} title="Dettaglio"
                        className="p-1.5 rounded-lg transition-colors" style={{ color: '#6b7280' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <Eye size={14} />
                      </button>
                      {(r.pdf_url || !isTecnico) && (
                        <button
                          onClick={() => handleDownloadPdf(r.id, r.wo_numero || String(r.id))}
                          title="Scarica PDF"
                          className="p-1.5 rounded-lg transition-colors"
                          style={{ color: '#1E40AF' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#dbeafe'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <Download size={14} />
                        </button>
                      )}
                      {!isTecnico && r.stato === 'inviato' && (
                        <button onClick={() => {
                          setInviaEmailCheck(true)
                          setApprovaModal({ id: r.id, work_order_id: r.work_order_id, committente_email: r.committente_email || null })
                        }} title="Approva"
                          className="p-1.5 rounded-lg transition-colors" style={{ color: '#15803d' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#dcfce7'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <CheckCircle size={14} />
                        </button>
                      )}
                      {!isTecnico && r.stato === 'bozza' && (
                        <button onClick={() => invia.mutate(r.id)} title="Invia per approvazione"
                          className="p-1.5 rounded-lg transition-colors" style={{ color: '#1d4ed8' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#dbeafe'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <Send size={14} />
                        </button>
                      )}
                      {!isTecnico && (r.stato === 'bozza' || r.stato === 'rifiutato') && (
                        <button onClick={() => confirmDelete('Eliminare?', () => elimina.mutate(r.id))} title="Elimina"
                          className="p-1.5 rounded-lg transition-colors" style={{ color: '#b91c1c' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#fee2e2'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ZIP download modal */}
      {zipModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl p-6 w-80">
            <h3 className="text-base font-semibold text-gray-800 mb-1">⬇️ Download ZIP Rapportini</h3>
            <p className="text-xs text-gray-500 mb-4">Scarica tutti i PDF del mese selezionato in un unico archivio.</p>
            <div className="space-y-3">
              <div>
                <label className="label">Anno</label>
                <select className="select w-full" value={zipAnno} onChange={e => setZipAnno(Number(e.target.value))}>
                  {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Mese</label>
                <select className="select w-full" value={zipMese} onChange={e => setZipMese(Number(e.target.value))}>
                  {MESI.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setZipModalOpen(false)} className="btn-secondary flex-1">Annulla</button>
              <button onClick={handleDownloadZip} disabled={zipLoading} className="btn-primary flex-1">
                {zipLoading ? '⏳ Download...' : '⬇️ Scarica ZIP'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {dettaglio && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(8,11,18,0.82)', backdropFilter: 'blur(4px)' }}>
          <div className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fade-up">
            <div className="p-5 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)' }}>
              <div>
                <h2 style={{ fontFamily: "'Barlow Semi Condensed', sans-serif", fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-100)' }}>
                  Rapportino <span style={{ fontFamily: "'JetBrains Mono', monospace", color: 'var(--signal)' }}>#{dettaglio.id}</span>
                </h2>
                <p style={{ fontSize: 11, color: 'var(--text-500)', marginTop: 2 }}>
                  WO-{dettaglio.work_order_id} · {format(new Date(dettaglio.data_intervento), 'dd MMMM yyyy', { locale: it })}
                </p>
              </div>
              <button onClick={() => setDettaglio(null)} className="btn-ghost px-2 py-1"><XCircle size={17} /></button>
            </div>
            <div className="p-5 space-y-5">
              {dettaglio.esito && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-600)', fontFamily: "'Barlow Semi Condensed', sans-serif" }}>Esito</span>
                  <Pill label={dettaglio.esito.replace(/_/g, ' ')} s={ESITO_STYLE[dettaglio.esito] ?? fallback} />
                </div>
              )}

              {dettaglio.descrizione_lavori && (
                <div>
                  <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-500)', fontFamily: "'Barlow Semi Condensed', sans-serif", marginBottom: 8 }}>
                    Descrizione Lavori
                  </p>
                  <p style={{ fontSize: 13, color: 'var(--text-300)', lineHeight: 1.7, padding: '10px 14px', borderRadius: 8, background: 'var(--ink-600)', border: '1px solid var(--border)' }}>
                    {dettaglio.descrizione_lavori}
                  </p>
                </div>
              )}

              {dettaglio.parametri_tecnici && Object.keys(dettaglio.parametri_tecnici).length > 0 && (
                <div>
                  <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-500)', fontFamily: "'Barlow Semi Condensed', sans-serif", marginBottom: 8 }}>
                    Parametri Tecnici
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(dettaglio.parametri_tecnici).map(([k, v]) => v != null && (
                      <div key={k} style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--ink-600)', border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 10, color: 'var(--text-500)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{k.replace(/_/g, ' ')}</div>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'var(--text-200)', fontWeight: 600 }}>{String(v)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {dettaglio.apparati_installati && dettaglio.apparati_installati.length > 0 && (
                <div>
                  <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-500)', fontFamily: "'Barlow Semi Condensed', sans-serif", marginBottom: 8 }}>
                    Apparati Installati
                  </p>
                  <div className="space-y-1.5">
                    {dettaglio.apparati_installati.map((a, i) => (
                      <div key={i} style={{ display: 'flex', gap: 12, padding: '8px 12px', borderRadius: 8, background: 'var(--ink-600)', border: '1px solid var(--border)' }}>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--signal)', fontWeight: 600 }}>{a.seriale}</span>
                        <span style={{ fontSize: 12, color: 'var(--text-400)' }}>{a.modello}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {dettaglio.firma_cliente_nome && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-600)', fontFamily: "'Barlow Semi Condensed', sans-serif" }}>Referente / Direttore</span>
                  <span style={{ fontSize: 13, color: 'var(--text-200)', fontWeight: 600 }}>{dettaglio.firma_cliente_nome}</span>
                </div>
              )}
              {dettaglio.note_aggiuntive && (
                <div>
                  <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-500)', fontFamily: "'Barlow Semi Condensed', sans-serif", marginBottom: 8 }}>
                    Note
                  </p>
                  <p style={{ fontSize: 13, color: 'var(--text-400)', lineHeight: 1.6, whiteSpace: 'pre-line', padding: '10px 14px', borderRadius: 8, background: 'var(--ink-600)', border: '1px solid var(--border)' }}>{dettaglio.note_aggiuntive}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Form modal */}
      {(showCreate || editing) && (
        <RapportinoFormModal
          rapportino={editing}
          onClose={() => { setShowCreate(false); setEditing(null) }}
          onSuccess={() => {
            setShowCreate(false); setEditing(null)
            qc.invalidateQueries({ queryKey: ['rapportini'] })
            toast.success(editing ? 'Aggiornato!' : 'Creato!')
          }}
        />
      )}

      {/* Dialog conferma approvazione */}
      {approvaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="card w-full max-w-sm p-6 space-y-4">
            <h3 className="font-semibold text-slate-800">Approva Rapportino</h3>
            <p className="text-sm text-slate-600">Vuoi approvare questo rapportino?</p>
            {approvaModal.committente_email ? (
              <label className="flex items-center gap-3 cursor-pointer p-3 bg-blue-50 rounded-lg border border-blue-100">
                <input
                  type="checkbox"
                  checked={inviaEmailCheck}
                  onChange={e => setInviaEmailCheck(e.target.checked)}
                  className="w-4 h-4"
                />
                <div>
                  <div className="text-sm font-semibold text-slate-800">Invia notifica al committente</div>
                  <div className="text-xs text-slate-500">{approvaModal.committente_email}</div>
                </div>
              </label>
            ) : (
              <div className="text-xs text-amber-700 bg-amber-50 rounded p-2">
                ⚠ Nessuna email configurata per questo committente.
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => setApprovaModal(null)} className="btn-secondary flex-1">Annulla</button>
              <button
                onClick={() => approva.mutate({
                  id: approvaModal.id,
                  work_order_id: approvaModal.work_order_id,
                  inviaEmail: approvaModal.committente_email ? inviaEmailCheck : false,
                })}
                disabled={approva.isPending}
                className="btn-primary flex-1">
                {approva.isPending ? 'Elaborazione...' : '✓ Approva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ── Form modal ──────────────────────────────────────────── */
function RapportinoFormModal({ rapportino, onClose, onSuccess }: {
  rapportino: RapportinoItem | null; onClose: () => void; onSuccess: () => void
}) {
  const isEdit = !!rapportino
  const [form, setForm] = useState({
    work_order_id: rapportino?.work_order_id?.toString() || '',
    data_intervento: rapportino?.data_intervento || format(new Date(), 'yyyy-MM-dd'),
    esito: rapportino?.esito || 'completato',
    descrizione_lavori: rapportino?.descrizione_lavori || '',
    note_aggiuntive: rapportino?.note_aggiuntive || '',
    km_percorsi: rapportino?.km_percorsi?.toString() || '0',
  })
  const f = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))

  const { data: woData } = useQuery({
    queryKey: ['wo-disponibili'],
    queryFn: () => workOrdersApi.list({ per_page: 100 }).then(r => r.data.items),
    enabled: !isEdit,
  })

  const createMutation = useMutation({
    mutationFn: (data: typeof form) => rapportiniApi.create({ ...data, work_order_id: Number(data.work_order_id), km_percorsi: Number(data.km_percorsi) }),
    onSuccess, onError: () => toast.error('Errore nella creazione'),
  })
  const updateMutation = useMutation({
    mutationFn: (data: typeof form) => rapportiniApi.update(rapportino!.id, {
      data_intervento: data.data_intervento, esito: data.esito,
      descrizione_lavori: data.descrizione_lavori, note_aggiuntive: data.note_aggiuntive,
      km_percorsi: Number(data.km_percorsi),
    }),
    onSuccess, onError: () => toast.error("Errore nell'aggiornamento"),
  })

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(8,11,18,0.82)', backdropFilter: 'blur(4px)' }}>
      <div className="card w-full max-w-lg animate-fade-up">
        <div className="p-5 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)' }}>
          <h2 style={{ fontFamily: "'Barlow Semi Condensed', sans-serif", fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-100)' }}>
            {isEdit ? 'Modifica Rapportino' : 'Nuovo Rapportino'}
          </h2>
          <button onClick={onClose} className="btn-ghost px-2 py-1"><XCircle size={16} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); isEdit ? updateMutation.mutate(form) : createMutation.mutate(form) }} className="p-5 space-y-4">
          {!isEdit ? (
            <div>
              <label className="label">Work Order *</label>
              <select className="select" value={form.work_order_id} onChange={(e) => f('work_order_id', e.target.value)} required>
                <option value="">Seleziona Work Order…</option>
                {woData?.map((wo: { id: number; numero_progressivo: string; titolo?: string }) => (
                  <option key={wo.id} value={wo.id}>{wo.numero_progressivo} — {wo.titolo}</option>
                ))}
              </select>
            </div>
          ) : (
            <div style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--ink-600)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--text-400)' }}>
              Work Order: <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#38bdf8' }}>WO-{rapportino?.work_order_id}</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Data intervento</label><input type="date" className="input" value={form.data_intervento} onChange={(e) => f('data_intervento', e.target.value)} /></div>
            <div>
              <label className="label">Esito *</label>
              <select className="select" value={form.esito} onChange={(e) => f('esito', e.target.value)}>
                {['completato','parzialmente_completato','non_completato'].map(e => (
                  <option key={e} value={e}>{e.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div><label className="label">Km percorsi</label><input type="number" className="input" style={{ fontFamily: "'JetBrains Mono', monospace" }} value={form.km_percorsi} onChange={(e) => f('km_percorsi', e.target.value)} /></div>
          </div>
          <div><label className="label">Descrizione lavori</label><textarea className="input" rows={4} value={form.descrizione_lavori} onChange={(e) => f('descrizione_lavori', e.target.value)} /></div>
          <div><label className="label">Note aggiuntive</label><textarea className="input" rows={2} value={form.note_aggiuntive} onChange={(e) => f('note_aggiuntive', e.target.value)} /></div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Annulla</button>
            <button type="submit" disabled={isPending} className="btn-primary flex-1">
              {isPending ? 'Salvataggio…' : isEdit ? 'Salva Modifiche' : 'Crea Rapportino'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
